import { A, CONTACT_EMAIL, H2, JURISDICTION, LegalLayout, MIN_AGE, OPERATOR, P, REGISTERED_ADDRESS, SITE_URL, Ul } from './shared';

// Public, unauthenticated route (see App.tsx) - same content as
// landing/templates.mjs's renderTerms(), ported to this app's components.
export function TermsScreen() {
  return (
    <LegalLayout title="Terms of Service">
      <P>
        These Terms of Service ("Terms") govern your access to and use of zmist - the website at {SITE_URL}, the web
        app you're using now, and the zmist iOS app (together, the "Service") - operated by {OPERATOR}. By creating
        an account or otherwise using the Service, you agree to these Terms. If you don't agree, don't use the
        Service.
      </P>

      <H2>1. What zmist is</H2>
      <P>
        zmist is a real-time messaging platform built around channels: a channel receives messages ("signals") sent
        either as free text or triggered from a reusable schema (a named preset with its own color, emoji, and
        optional value). Messages relay live over zmist's own real-time protocol and are also stored so they're
        visible to anyone who later opens the channel. A channel owner can generate a public, read-only share link
        (in the form {SITE_URL}/shared/&lt;channel&gt;/&lt;token&gt;) that lets anyone with the link view that
        channel's messages and charts without creating an account - see Section 6 and our{' '}
        <A to="/privacy">Privacy Policy</A> for what that means for anything posted there.
      </P>

      <H2>2. Accounts</H2>
      <P>
        You need an account to create channels, join them, or send messages. You're responsible for the activity
        that happens under your account and for keeping your credentials (including any password reset link we
        email you) confidential. Tell us at {CONTACT_EMAIL} if you believe your account has been compromised.
      </P>
      <P>
        You may be able to create or sign into an account using a third-party identity provider (Google, Microsoft,
        Apple, or LinkedIn) where we've enabled it. Using one of those means you're also agreeing to that provider's
        own terms - we only ever receive your name and email from them, nothing more.
      </P>
      <P>You must be at least {MIN_AGE} years old to use zmist.</P>

      <H2>3. Acceptable use</H2>
      <P>Don't use zmist to:</P>
      <Ul>
        <li>Send content that's illegal, infringing, harassing, or that you don't have the right to share;</li>
        <li>Attempt to gain unauthorized access to another account, channel, or to zmist's own systems;</li>
        <li>
          Interfere with or overload the Service (including its real-time signaling protocol) or attempt to
          reverse-engineer it beyond what applicable law allows;
        </li>
        <li>Use zmist to send spam, malware, or unsolicited bulk messages, including via the schema-signal or channel-invite-email features.</li>
      </Ul>
      <P>We can suspend or terminate access for violating this section, at our discretion, with or without notice depending on severity.</P>

      <H2>4. Your content</H2>
      <P>
        You keep ownership of whatever you send through zmist. By sending it, you give us the license we need to
        store, relay, and display it back to you and to whoever else has access to that channel (including anyone
        with a public share link, if one exists) - that's it, just what running the Service requires. You're
        responsible for having the rights to anything you send.
      </P>

      <H2>5. Channel invitations</H2>
      <P>
        If you invite someone to a channel by email, you're confirming you have a legitimate reason to contact them
        and that doing so doesn't violate any law or agreement that applies to you.
      </P>

      <H2>6. Public share links</H2>
      <P>
        A share link is intentionally public to anyone who has it - it isn't protected by your account credentials,
        and (depending on the channel's own settings) it may or may not require a separate password. Don't generate
        or share one for content you don't want visible to whoever ends up with that link. Revoking or regenerating
        a share link stops the old one from working going forward, but doesn't retract anything already viewed or
        copied while it was live.
      </P>

      <H2>7. Service availability</H2>
      <P>
        We aim to keep zmist available and reliable but don't guarantee it will always be - real-time messaging
        depends on network conditions we don't control, and we may need to take the Service down for maintenance or
        make changes to it (including to features described in Section 1) from time to time.
      </P>

      <H2>8. Termination</H2>
      <P>
        You can stop using zmist and delete your account at any time from Settings &rsaquo; Profile in the app
        (deactivated immediately, permanently erased after a 7-day grace period - see our{' '}
        <A to="/privacy">Privacy Policy</A>), or by contacting {CONTACT_EMAIL}. We may suspend or terminate your
        access for violating these Terms.
      </P>

      <H2>9. Disclaimers and limitation of liability</H2>
      <P>
        The Service is provided "as is," without warranties of any kind, to the maximum extent the law allows. To
        that same extent, {OPERATOR} isn't liable for indirect, incidental, or consequential damages arising from
        your use of the Service.
      </P>

      <H2>10. Changes to these Terms</H2>
      <P>
        We may update these Terms as zmist changes. We'll update the date at the top of this page when we do;
        continuing to use the Service after a change means you accept the update.
      </P>

      <H2>11. Governing law</H2>
      <P>These Terms are governed by the laws of {JURISDICTION}, without regard to its conflict-of-law rules.</P>

      <H2>12. Contact</H2>
      <P>
        Questions about these Terms: {CONTACT_EMAIL}
        {REGISTERED_ADDRESS ? <> · {REGISTERED_ADDRESS}</> : null}
      </P>
    </LegalLayout>
  );
}
